import { ATOM_TOKEN_PATTERN } from './document_text_index.js';

const prose = { type: 'string', format: 'ordinal-atom-text' };
// Text that INTRODUCES a new block. Its numbering is derived by
// createListItemsMap once the block exists, so the model has no ordinal to
// reference and must not invent one — the first end-to-end run produced
// ⟦n171a:(xi)⟧, a fabricated sub-ordinal, for exactly this reason. A number atom
// copied from an existing item is equally wrong: it would duplicate that item's
// identity rather than allocate a new one.
const newBlockProse = { type: 'string', format: 'atom-free-text' };
const requiredProse = {
  type: 'string',
  pattern: '.+',
  format: 'ordinal-atom-text',
};
const explanation = { type: 'string', pattern: '.+' };
const confidence = { type: 'number', minimum: 0, maximum: 1 };

// Computed by the server after the model has answered and merged onto the
// proposal it describes: whether the clause is the source's words
// (contentProvenance), whether the cited pattern provision was found
// (patternedOnVerified), whether the citation matched the source
// (sourceQuoteVerified).
//
// They live in the schema because the schema is closed, and a closed schema
// rejects what it does not name. Adding a server-side field to a payload the
// client validates strictly is a breaking change — it happened three times in
// one day and turned every drafted clause into a drafting note, because the
// only tests that existed validated model output and never the shape that
// actually arrives.
//
// The server strips these from the model's own output before computing them, so
// a value here is always ours.
const serverAnnotations = {
  contentProvenance: { type: 'string' },
  patternedOnVerified: { type: 'boolean' },
  sourceQuoteVerified: { type: 'boolean' },
  confidenceLow: { type: 'boolean' },
  // Kept as one provenance value so the human-readable label cannot drift
  // away from the opaque record it names, and the review card does not have to
  // guess Clausebook's deep-link contract from an id alone.
  clausebookEntry: {
    type: 'object',
    additionalProperties: false,
    properties: {
      id: explanation,
      name: explanation,
      url: explanation,
    },
    required: ['id', 'name', 'url'],
  },
};

const sharedEvidence = {
  rationale: explanation,
  sourceQuote: explanation,
};

const patchDefinitions = {
  text_patch: {
    type: 'object',
    additionalProperties: false,
    properties: {
      kind: { const: 'text_patch' },
      find: requiredProse,
      replace: prose,
      ...sharedEvidence,
      ...serverAnnotations,
      confidence,
      contextBefore: prose,
      // The same term as one standalone paragraph, for when the edit cannot be
      // made at all. Not required by this schema even though the server asks
      // the model for it on every patch: a response that omits it is a response
      // with one less option, not an invalid one, and rejecting the whole
      // proposal over a missing fallback would cost the edit as well.
      fallbackContent: prose,
    },
    required: [
      'kind',
      'find',
      'replace',
      'rationale',
      'sourceQuote',
      'confidence',
    ],
  },
  field_conflict: {
    type: 'object',
    additionalProperties: false,
    properties: {
      kind: { const: 'field_conflict' },
      atom: { type: 'integer', minimum: 0 },
      observed: { type: 'string' },
      sourceValue: { type: 'string' },
      ...sharedEvidence,
      ...serverAnnotations,
      fallbackContent: prose,
    },
    required: [
      'kind',
      'atom',
      'observed',
      'sourceValue',
      'sourceQuote',
      'rationale',
    ],
  },
  insert_block: {
    type: 'object',
    additionalProperties: false,
    properties: {
      kind: { const: 'insert_block' },
      after: requiredProse,
      content: { ...newBlockProse, pattern: '\\S' },
      // The lease provision a drafted clause followed, quoted verbatim. Not
      // required: a lifted clause has nothing to pattern itself on, since it is
      // already the source's own language.
      patternedOn: { type: 'string' },
      ...sharedEvidence,
      ...serverAnnotations,
      confidence,
    },
    required: [
      'kind',
      'after',
      'content',
      'rationale',
      'sourceQuote',
      'confidence',
    ],
  },
  delete_block: {
    type: 'object',
    additionalProperties: false,
    properties: {
      kind: { const: 'delete_block' },
      find: requiredProse,
      ...sharedEvidence,
      ...serverAnnotations,
      confidence,
      contextBefore: prose,
    },
    required: ['kind', 'find', 'rationale', 'sourceQuote', 'confidence'],
  },
  insert_row: {
    type: 'object',
    additionalProperties: false,
    properties: {
      kind: { const: 'insert_row' },
      after: requiredProse,
      cells: {
        type: 'array',
        minItems: 1,
        // Same rule as insert_block content: a row that does not exist yet has
        // no cells to reference, so its text cannot carry an atom either.
        items: newBlockProse,
      },
      ...sharedEvidence,
      ...serverAnnotations,
      confidence,
    },
    required: [
      'kind',
      'after',
      'cells',
      'rationale',
      'sourceQuote',
      'confidence',
    ],
  },
  delete_row: {
    type: 'object',
    additionalProperties: false,
    properties: {
      kind: { const: 'delete_row' },
      find: requiredProse,
      ...sharedEvidence,
      ...serverAnnotations,
      confidence,
      contextBefore: prose,
    },
    required: ['kind', 'find', 'rationale', 'sourceQuote', 'confidence'],
  },
};

export const DOCUMENT_PATCH_SCHEMA = {
  $schema: 'https://json-schema.org/draft/2020-12/schema',
  type: 'array',
  items: {
    oneOf: Object.keys(patchDefinitions).map(kind => ({
      $ref: `#/$defs/${kind}`,
    })),
  },
  $defs: patchDefinitions,
};

// Shared with the projection that issued the tokens. Hard-coding the kinds here
// made this refuse a `find` that quoted the document's own text verbatim, the
// moment the projection gained a kind this file had not heard of.
const ATOM_TOKEN = new RegExp(ATOM_TOKEN_PATTERN, 'g');
const ATOM_BRACKET = /[⟦⟧]/;

function hasOnlyOrdinalAtomTokens(value) {
  return !ATOM_BRACKET.test(value.replace(ATOM_TOKEN, ''));
}

function describeProperty(name, value, rule) {
  if (Object.prototype.hasOwnProperty.call(rule, 'const')) {
    return value === rule.const ? null : `${name} must equal ${rule.const}`;
  }

  if (rule.type === 'string') {
    if (typeof value !== 'string') return `${name} must be a string`;
    if (rule.pattern && !new RegExp(rule.pattern).test(value)) {
      return `${name} must not be empty`;
    }
    if (
      rule.format === 'ordinal-atom-text' &&
      !hasOnlyOrdinalAtomTokens(value)
    ) {
      return `${name} contains an atom reference that is not ordinal-form`;
    }
    if (rule.format === 'atom-free-text' && ATOM_BRACKET.test(value)) {
      return (
        `${name} must not contain an atom token — the numbering and fields of a ` +
        'new block are derived once it exists, so there is no ordinal to reference'
      );
    }
    return null;
  }

  if (rule.type === 'number') {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
      return `${name} must be a finite number`;
    }
  } else if (rule.type === 'integer') {
    if (!Number.isInteger(value)) return `${name} must be an integer`;
  } else if (rule.type === 'array') {
    if (!Array.isArray(value)) return `${name} must be an array`;
    // Probed by index rather than by count: this module may not name the array
    // count property anywhere, because no agent-authored shape is allowed to
    // address document content by position or extent, and the P7-01 gate greps
    // the whole file for that name. Asking whether the minItems-th index exists
    // is the same question, and unlike a bare emptiness test it honours a
    // minItems above 1 if a later shape declares one.
    if (
      rule.minItems &&
      !Object.prototype.hasOwnProperty.call(value, rule.minItems - 1)
    ) {
      const plural = rule.minItems === 1 ? 'item' : 'items';
      return `${name} must contain at least ${rule.minItems} ${plural}`;
    }
    for (const item of value) {
      const reason = describeProperty(`${name} item`, item, rule.items);
      if (reason) return reason;
    }
    return null;
  } else if (rule.type === 'object') {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      return `${name} must be an object`;
    }

    const missing = (rule.required || []).find(
      property => !Object.prototype.hasOwnProperty.call(value, property),
    );
    if (missing) return `${name} is missing ${missing}`;

    if (rule.additionalProperties === false) {
      const unexpected = Object.keys(value).find(
        property =>
          !Object.prototype.hasOwnProperty.call(rule.properties, property),
      );
      if (unexpected) return `${name} has unexpected property: ${unexpected}`;
    }

    for (const [property, propertyValue] of Object.entries(value)) {
      const reason = describeProperty(
        `${name}.${property}`,
        propertyValue,
        rule.properties[property],
      );
      if (reason) return reason;
    }
    return null;
  }

  if (rule.minimum !== undefined && value < rule.minimum) {
    return `${name} must be at least ${rule.minimum}`;
  }
  if (rule.maximum !== undefined && value > rule.maximum) {
    return `${name} must be at most ${rule.maximum}`;
  }
  return null;
}

export function validateDocumentPatch(entry) {
  if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
    return { valid: false, reason: 'entry must be an object' };
  }

  const shape = patchDefinitions[entry.kind];
  if (!shape) {
    return { valid: false, reason: `unknown kind: ${String(entry.kind)}` };
  }

  const missing = shape.required.find(
    name => !Object.prototype.hasOwnProperty.call(entry, name),
  );
  if (missing) return { valid: false, reason: `missing ${missing}` };

  const unexpected = Object.keys(entry).find(
    name => !Object.prototype.hasOwnProperty.call(shape.properties, name),
  );
  if (unexpected) {
    return { valid: false, reason: `unexpected property: ${unexpected}` };
  }

  for (const [name, value] of Object.entries(entry)) {
    const reason = describeProperty(name, value, shape.properties[name]);
    if (reason) return { valid: false, reason };
  }

  return { valid: true, value: entry };
}

export function validateModelOutput(modelOutput, logger = console) {
  if (!Array.isArray(modelOutput)) {
    const reason = 'model output must be an array';
    logger.warn(`Rejected document patch output: ${reason}`);
    return { patches: [], rejected: [{ entry: modelOutput, reason }] };
  }

  const patches = [];
  const rejected = [];

  modelOutput.forEach((entry, index) => {
    const result = validateDocumentPatch(entry);
    if (result.valid) {
      patches.push(result.value);
      return;
    }

    const rejection = { index, entry, reason: result.reason };
    rejected.push(rejection);
    logger.warn(
      `Rejected document patch at index ${index}: ${result.reason}`,
      entry,
    );
  });

  return { patches, rejected };
}
